import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { Security } from '../../../proto/generated/security'

type Props = {
    security?: Security | null
}

// A quiet silhouette of an option chain: calls OI to the left of the strike column, puts OI to the
// right, longest around the ATM row, so the empty state reads as "the chain that isn't there".
// [callBarWidth, putBarWidth] per row in a 136px-wide half, brightest at the centre.
const ROWS: Array<[number, number]> = [
    [34, 52],
    [46, 40],
    [60, 72],
    [88, 64],
    [118, 96],
    [92, 124],
    [70, 84],
    [52, 66],
    [38, 44]
]
const ROW_H = 9
const ROW_GAP = 1
const STRIKE_W = 24
const HALF_W = 128
const CENTER = (ROWS.length - 1) / 2

const GhostOptionChain = () => (
    <svg
        viewBox={`0 0 ${HALF_W * 2 + STRIKE_W} ${ROWS.length * (ROW_H + ROW_GAP)}`}
        width="280"
        aria-hidden="true"
        style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
    >
        {ROWS.map(([call, put], i) => {
            const y = i * (ROW_H + ROW_GAP)
            const opacity = Math.max(0.12, 0.55 - Math.abs(i - CENTER) * 0.1)
            return (
                <g key={i} opacity={opacity} stroke="currentColor" strokeWidth="1" fill="none">
                    <rect x={HALF_W - call + 0.5} y={y + 0.5} width={call - 4} height={ROW_H - 1} rx="1" />
                    <line x1={HALF_W + 6} y1={y + ROW_H / 2} x2={HALF_W + STRIKE_W - 6} y2={y + ROW_H / 2} />
                    <rect x={HALF_W + STRIKE_W + 3.5} y={y + 0.5} width={put - 4} height={ROW_H - 1} rx="1" />
                </g>
            )
        })}
    </svg>
)

const getMessage = (security?: Security | null) => {
    const symbol = security ? security.symbol || security.displayName || security.code : ''
    if (!symbol) {
        return {
            title: 'No option chain to show.',
            caption: 'Pick an F&O underlying to see its option chain.'
        }
    }
    if (security?.hasFo === false) {
        return {
            title: `${symbol} has no option chain.`,
            caption: 'Options are not traded on this security. Pick an F&O underlying to see its chain.'
        }
    }
    if (security?.hasFo) {
        return {
            title: `No option chain for ${symbol} right now.`,
            caption: 'No active expiries or live quotes came back. The symbol may be illiquid; try again in a while.'
        }
    }
    return {
        title: `No option chain for ${symbol}.`,
        caption: 'Option chain data is not available for this security.'
    }
}

const OptionChainEmptyState: React.FC<Props> = ({ security }) => {
    const { title, caption } = getMessage(security)
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: 200,
                px: 2,
                color: 'text.secondary'
            }}
        >
            <Stack alignItems="center" sx={{ maxWidth: 360 }}>
                <GhostOptionChain />
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2.5 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.disabled" align="center" sx={{ mt: 0.5 }}>
                    {caption}
                </Typography>
            </Stack>
        </Box>
    )
}

export default OptionChainEmptyState
